// Tutorial content for all modules
// Each module has: id, title, description, steps (for walkthrough), and fullGuide (for Learn More page)

export const tutorials = {
  products: {
    id: 'products',
    title: 'Products',
    icon: 'ShoppingBag',
    category: 'Getting Started',
    description: 'Manage your product catalog with material costing and AI-powered pricing.',
    steps: [
      {
        target: '[data-testid="add-product-btn"]',
        title: 'Add Your First Product',
        content: 'Click here to create a new product. Include details like materials, labor time, and pricing.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="product-search"]',
        title: 'Search Products',
        content: 'Quickly find products by name, SKU, or category using the search bar.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="product-card"]',
        title: 'Product Cards',
        content: 'Each card shows key info at a glance. Click to edit, or use the menu for more options.',
        placement: 'right'
      },
      {
        target: '[data-testid="ai-pricing-btn"]',
        title: 'AI Pricing Assistant',
        content: 'Get smart pricing suggestions based on your materials, labor, and market data.',
        placement: 'left'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Creating Products',
          content: 'Products are the items you sell. Each product can include materials (from your Materials library), labor time, and skill level to calculate accurate costs. Add photos to showcase your work.'
        },
        {
          title: 'Material Costs',
          content: 'Link materials to products with specific quantities. The system automatically calculates your material cost based on current prices. Update material prices once, and all product costs update automatically.'
        },
        {
          title: 'Labor & Pricing',
          content: 'Set your hourly rate in Settings, then add labor time to each product. Combined with material costs, this gives you a true cost basis for pricing decisions.'
        },
        {
          title: 'AI Pricing Assistant',
          content: 'Our AI analyzes your costs, comparable products, and market trends to suggest optimal pricing. It considers your target profit margin and provides reasoning for each recommendation.'
        },
        {
          title: 'Categories & Organization',
          content: 'Organize products into categories for easy filtering. Create custom categories that match how you think about your inventory.'
        }
      ],
      tips: [
        'Take consistent product photos on a neutral background for a professional look',
        'Review and update material costs quarterly to keep pricing accurate',
        'Use the AI Pricing Assistant when launching new products or adjusting for inflation'
      ]
    }
  },

  materials: {
    id: 'materials',
    title: 'Materials',
    icon: 'Package',
    category: 'Getting Started',
    description: 'Track raw materials, costs, and suppliers for accurate product costing.',
    steps: [
      {
        target: '[data-testid="add-material-btn"]',
        title: 'Add Materials',
        content: 'Add the raw materials you use. Include unit cost and supplier info.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="material-card"]',
        title: 'Material Details',
        content: 'View and edit material details including cost per unit, supplier, and usage in products.',
        placement: 'right'
      },
      {
        target: '[data-testid="material-search"]',
        title: 'Find Materials',
        content: 'Search your material library by name or supplier.',
        placement: 'bottom'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Setting Up Materials',
          content: 'Materials are the building blocks of your products. Track everything from beads and wire to fabric and wood. Include the unit of measurement (oz, yards, pieces) and cost per unit.'
        },
        {
          title: 'Linking to Products',
          content: 'When creating products, you\'ll select materials and specify quantities. This creates a bill of materials for each product, enabling accurate cost calculations.'
        },
        {
          title: 'Managing Suppliers',
          content: 'Track where you buy each material. This helps when reordering and comparing prices across suppliers.'
        },
        {
          title: 'Cost Updates',
          content: 'When material prices change, update them here. All products using that material will automatically reflect the new cost.'
        }
      ],
      tips: [
        'Include shipping costs in your material unit cost for accuracy',
        'Set up materials before creating products for smoother workflow',
        'Review material costs when suppliers run promotions to capture savings'
      ]
    }
  },

  sales: {
    id: 'sales',
    title: 'Sales',
    icon: 'DollarSign',
    category: 'Sales & Finance',
    description: 'Log and track sales from events, online shops, and direct sales.',
    steps: [
      {
        target: '[data-testid="add-sale-btn"]',
        title: 'Record a Sale',
        content: 'Log sales as they happen. Select the product, channel, and payment method.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="sales-channel-tabs"]',
        title: 'Sales Channels',
        content: 'View sales by channel: Events, Etsy, Website, or Direct sales.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="sales-filters"]',
        title: 'Filter & Analyze',
        content: 'Filter by date range, product, or use "Group by Event" to see event performance.',
        placement: 'right'
      },
      {
        target: '[data-testid="sale-card"]',
        title: 'Sale Details',
        content: 'Each sale shows product, amount, channel, and event (if applicable). Click to edit or delete.',
        placement: 'right'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Recording Sales',
          content: 'Capture every sale with product, quantity, price, and channel. For event sales, link to the specific event to track performance. Add payment method for cash flow insights.'
        },
        {
          title: 'Sales Channels',
          content: 'Track sales across all your channels: craft shows, Etsy, your website, boutiques, and direct/custom orders. Each channel has its own view for focused analysis.'
        },
        {
          title: 'Event Sales',
          content: 'Link sales to events to calculate event profitability. The Analytics page uses this data to show which events perform best.'
        },
        {
          title: 'Filtering & Grouping',
          content: 'Use date filters to view specific periods. The "Group by Event" feature aggregates sales to show total revenue per event.'
        },
        {
          title: 'Etsy Integration',
          content: 'Connect your Etsy shop to automatically import orders. Sales are matched to your products for seamless tracking.'
        }
      ],
      tips: [
        'Record sales immediately at events using your phone',
        'Use the Quick Add feature for repeat sales of popular items',
        'Review weekly sales to spot trends and adjust inventory'
      ]
    }
  },

  boutiques: {
    id: 'boutiques',
    title: 'Boutiques',
    icon: 'Building2',
    category: 'Sales & Finance',
    description: 'Manage consignment relationships with local shops and boutiques.',
    steps: [
      {
        target: '[data-testid="add-boutique-btn"]',
        title: 'Add a Boutique',
        content: 'Add shops where you consign products. Include contact info and commission rate.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="boutique-card"]',
        title: 'Boutique Details',
        content: 'View products on consignment, sales, and commission owed at each location.',
        placement: 'right'
      },
      {
        target: '[data-testid="consignment-products"]',
        title: 'Consignment Products',
        content: 'Track which products are at each boutique and their quantities.',
        placement: 'bottom'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Setting Up Boutiques',
          content: 'Add each shop where you sell on consignment. Include the shop name, contact, address, and their commission percentage (typically 30-50%).'
        },
        {
          title: 'Consignment Inventory',
          content: 'Track what products are at each location. When you deliver items, add them to the boutique\'s inventory. When they sell, record the sale and inventory updates automatically.'
        },
        {
          title: 'Commission Tracking',
          content: 'The system calculates commission owed based on sales and your agreed rate. Use this for monthly reconciliation with each boutique.'
        },
        {
          title: 'Restocking',
          content: 'Monitor inventory levels at each boutique. Low stock alerts help you know when to restock popular items.'
        }
      ],
      tips: [
        'Keep a delivery receipt signed by the boutique when dropping off consignment',
        'Review boutique performance monthly to focus on your best relationships',
        'Negotiate better commission rates as your sales volume increases'
      ]
    }
  },

  inventory: {
    id: 'inventory',
    title: 'Inventory',
    icon: 'Layers',
    category: 'Sales & Finance',
    description: 'Unified inventory tracking across all sales channels.',
    steps: [
      {
        target: '[data-testid="inventory-overview"]',
        title: 'Inventory Overview',
        content: 'See total inventory across all locations: on-hand, at boutiques, and allocated to events.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="inventory-item"]',
        title: 'Stock Levels',
        content: 'Each product shows current stock, location breakdown, and reorder status.',
        placement: 'right'
      },
      {
        target: '[data-testid="inventory-adjust"]',
        title: 'Adjust Inventory',
        content: 'Update quantities when you make new products or do inventory counts.',
        placement: 'left'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Inventory Tracking',
          content: 'The Inventory module shows a unified view of all your products across locations. See what\'s on-hand in your studio, at boutiques, or allocated for upcoming events.'
        },
        {
          title: 'Location Breakdown',
          content: 'Click any product to see exactly where inventory is located. This helps when deciding what to bring to events or restock at boutiques.'
        },
        {
          title: 'Stock Adjustments',
          content: 'Add inventory when you complete new products. Subtract for samples, gifts, or inventory corrections. All changes are logged with reasons.'
        },
        {
          title: 'Low Stock Alerts',
          content: 'Set reorder points for products. When stock drops below the threshold, you\'ll see a low stock warning to prompt production or reordering.'
        },
        {
          title: 'Inventory Settings',
          content: 'In Settings, you can enable "Allow Negative Inventory" for made-to-order items, or keep it strict for physical inventory tracking.'
        }
      ],
      tips: [
        'Do a full inventory count monthly to catch discrepancies early',
        'Use the event allocation feature before shows to reserve inventory',
        'Set reorder points based on how long products take to make'
      ]
    }
  },

  expenses: {
    id: 'expenses',
    title: 'Expenses',
    icon: 'Receipt',
    category: 'Sales & Finance',
    description: 'Track business expenses, event fees, and costs for profitability analysis.',
    steps: [
      {
        target: '[data-testid="add-expense-btn"]',
        title: 'Add Expense',
        content: 'Record business expenses with date, category, and optional event link.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="expense-categories"]',
        title: 'Expense Categories',
        content: 'Organize expenses by type: supplies, booth fees, travel, marketing, etc.',
        placement: 'right'
      },
      {
        target: '[data-testid="expense-list"]',
        title: 'Expense History',
        content: 'View and filter past expenses. Link event expenses for accurate profitability.',
        placement: 'bottom'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Recording Expenses',
          content: 'Track all business expenses to understand your true profitability. Include date, amount, category, and description. Link to events when applicable.'
        },
        {
          title: 'Event Expenses',
          content: 'Link expenses like booth fees, travel, and lodging to specific events. This data feeds into event profitability calculations in Analytics.'
        },
        {
          title: 'Expense Categories',
          content: 'Use categories for organization and reporting: Supplies, Equipment, Travel, Booth Fees, Marketing, Professional Services, and more.'
        },
        {
          title: 'Tax Preparation',
          content: 'Well-categorized expenses make tax time easier. Export expense data for your accountant or import into tax software.'
        }
      ],
      tips: [
        'Photograph receipts and add notes when recording expenses',
        'Review monthly to catch any missing expenses while memory is fresh',
        'Track mileage as a separate expense for accurate travel deductions'
      ]
    }
  },

  media: {
    id: 'media',
    title: 'Media Library',
    icon: 'Image',
    category: 'Media & Files',
    description: 'Organize photos and media for products and event applications.',
    steps: [
      {
        target: '[data-testid="upload-media-btn"]',
        title: 'Upload Media',
        content: 'Upload product photos, booth shots, and application images to your library.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="media-grid"]',
        title: 'Media Gallery',
        content: 'Browse and organize your images. Use tags and categories for easy finding.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="media-item"]',
        title: 'Media Details',
        content: 'Click any image to view details, add tags, or copy the link for applications.',
        placement: 'right'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Building Your Library',
          content: 'Upload high-quality images of your products, booth setup, and works in progress. A well-organized library makes event applications faster and more professional.'
        },
        {
          title: 'Image Organization',
          content: 'Use tags to categorize images: products, booth photos, process shots, headshots, etc. This makes finding the right image for applications quick and easy.'
        },
        {
          title: 'Application Images',
          content: 'Many event applications require specific images. Keep your best booth photos, product displays, and artist photos ready to use.'
        },
        {
          title: 'Product Photos',
          content: 'Link images to products for use in sales channels and inventory management. Consistent, professional photos improve sales.'
        }
      ],
      tips: [
        'Keep both square and landscape versions of key images for different application requirements',
        'Update booth photos annually to show your current display',
        'Include process/making shots to tell your artisan story'
      ]
    }
  },

  analytics: {
    id: 'analytics',
    title: 'Analytics',
    icon: 'BarChart3',
    category: 'Sales & Finance',
    description: 'Dashboards and insights to understand business performance.',
    steps: [
      {
        target: '[data-testid="analytics-overview"]',
        title: 'Performance Overview',
        content: 'See key metrics at a glance: total revenue, profit, and growth trends.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="time-range-selector"]',
        title: 'Time Period',
        content: 'Change the time range to analyze different periods: week, month, year, or all time.',
        placement: 'left'
      },
      {
        target: '[data-testid="channel-breakdown"]',
        title: 'Channel Analysis',
        content: 'See which sales channels perform best and where to focus your efforts.',
        placement: 'right'
      },
      {
        target: '[data-testid="event-performance"]',
        title: 'Event Performance',
        content: 'Compare events by revenue, profit, and ROI to inform future decisions.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="series-performance"]',
        title: 'Series Trends',
        content: 'Track recurring events year-over-year to see long-term trends.',
        placement: 'bottom'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Understanding Your Dashboard',
          content: 'The Analytics dashboard aggregates data from sales, expenses, and events to show your true business performance. Key metrics include total revenue, gross profit, and growth rates.'
        },
        {
          title: 'Channel Performance',
          content: 'See how each sales channel contributes to your business. Compare events, Etsy, website, and boutique sales to understand where to invest your time.'
        },
        {
          title: 'Event ROI',
          content: 'Events are analyzed by revenue minus expenses (booth fee, travel, supplies) to show true profit. Sort by ROI to find your most profitable shows.'
        },
        {
          title: 'Series Analysis',
          content: 'For recurring events, the Series Performance section shows year-over-year trends. See if events are growing, stable, or declining over time.'
        },
        {
          title: 'Product Insights',
          content: 'Discover your best-selling products and highest-margin items. Use this to guide production decisions and pricing adjustments.'
        }
      ],
      tips: [
        'Review analytics monthly to catch trends early',
        'Compare similar events to decide which to prioritize for applications',
        'Use channel data to balance your sales mix for stability'
      ]
    }
  },

  whatif: {
    id: 'whatif',
    title: 'What-If Planner',
    icon: 'Calculator',
    category: 'Sales & Finance',
    description: 'Model scenarios to plan events and predict profitability.',
    steps: [
      {
        target: '[data-testid="create-scenario-btn"]',
        title: 'Create Scenario',
        content: 'Model a potential event with estimated sales, expenses, and costs.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="scenario-inputs"]',
        title: 'Scenario Details',
        content: 'Enter booth cost, expected sales, travel expenses, and other factors.',
        placement: 'right'
      },
      {
        target: '[data-testid="scenario-results"]',
        title: 'Projected Results',
        content: 'See projected profit, ROI, and break-even point for your scenario.',
        placement: 'left'
      },
      {
        target: '[data-testid="compare-scenarios"]',
        title: 'Compare Options',
        content: 'Save multiple scenarios to compare events and make informed decisions.',
        placement: 'bottom'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Scenario Planning',
          content: 'The What-If Planner helps you evaluate events before committing. Model different scenarios with various assumptions to understand potential outcomes.'
        },
        {
          title: 'Input Factors',
          content: 'Enter booth cost, expected daily sales, number of days, travel costs, lodging, and any other expenses. The more accurate your inputs, the better your projections.'
        },
        {
          title: 'Using Historical Data',
          content: 'Base estimates on past performance at similar events. The system can suggest values based on your Analytics data for comparable shows.'
        },
        {
          title: 'Comparing Events',
          content: 'Create scenarios for multiple events you\'re considering. Compare projected ROI to decide where to apply and which shows to prioritize.'
        },
        {
          title: 'Break-Even Analysis',
          content: 'See how much you need to sell to cover costs. This helps set realistic goals and evaluate whether an event is worth the investment.'
        }
      ],
      tips: [
        'Create scenarios before applying to expensive shows',
        'Build conservative, expected, and optimistic versions of each scenario',
        'Update scenarios with actual results after events to improve future estimates'
      ]
    }
  },

  applications: {
    id: 'applications',
    title: 'Applications',
    icon: 'FileText',
    category: 'Events & Planning',
    description: 'Manage event applications with reusable profiles and tracking.',
    steps: [
      {
        target: '[data-testid="create-application-btn"]',
        title: 'New Application',
        content: 'Start an application for an event. Use saved profiles to speed up the process.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="application-profiles"]',
        title: 'Application Profiles',
        content: 'Create reusable profiles with your bio, descriptions, and common answers.',
        placement: 'right'
      },
      {
        target: '[data-testid="application-status"]',
        title: 'Track Status',
        content: 'Monitor application status: draft, submitted, waitlisted, accepted, or declined.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="application-deadlines"]',
        title: 'Deadlines',
        content: 'Never miss a deadline with application due date tracking.',
        placement: 'left'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Application Workflow',
          content: 'Track every event application from discovery to decision. Save drafts, submit, and monitor status all in one place.'
        },
        {
          title: 'Reusable Profiles',
          content: 'Create profiles with your artist statement, product descriptions, and common Q&A answers. Apply them to new applications to save time and ensure consistency.'
        },
        {
          title: 'Media Attachments',
          content: 'Link images from your Media Library to applications. Keep booth photos, product images, and headshots ready to use.'
        },
        {
          title: 'Status Tracking',
          content: 'Update status as applications progress: Draft → Submitted → Under Review → Accepted/Declined/Waitlisted. Filter by status to focus on what needs attention.'
        },
        {
          title: 'Deadline Management',
          content: 'Add application deadlines to stay organized. The Dashboard shows upcoming deadlines to prevent missed opportunities.'
        }
      ],
      tips: [
        'Apply early - many juried shows have limited spots',
        'Keep your profiles updated with fresh accomplishments and photos',
        'Track declined applications to identify patterns and improve future submissions'
      ]
    }
  },

  eventscout: {
    id: 'eventscout',
    title: 'Event Scout',
    icon: 'Sparkles',
    category: 'Events & Planning',
    description: 'Discover new events with AI-powered matching and recommendations.',
    steps: [
      {
        target: '[data-testid="event-search-input"]',
        title: 'Search Events',
        content: 'Search for events by location, keyword, or category.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="search-filters"]',
        title: 'Refine Results',
        content: 'Filter by distance, date range, event type, and more.',
        placement: 'right'
      },
      {
        target: '[data-testid="match-score"]',
        title: 'AI Match Scores',
        content: 'See how well each event matches your profile. Higher scores mean better fit.',
        placement: 'left'
      },
      {
        target: '[data-testid="save-event-btn"]',
        title: 'Save Events',
        content: 'Save interesting events to your list for later review and application.',
        placement: 'bottom'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Finding Events',
          content: 'Event Scout searches our database of craft shows, markets, and festivals. Enter a location and keywords to find relevant events in your area or regions you want to explore.'
        },
        {
          title: 'AI Matching',
          content: 'Set up your Artisan Profile in Settings to enable AI matching. The system analyzes event descriptions against your craft type, preferences, and past success to generate match scores.'
        },
        {
          title: 'Understanding Match Scores',
          content: 'Scores range from 0-100%. Events marked "Best Match" or "Great Fit" align well with your profile. Review the match reasons to understand why an event scored high or low.'
        },
        {
          title: 'Saving & Organizing',
          content: 'Save events you\'re interested in to review later. Add notes and rate your interest level. When ready, start an application directly from the saved event.'
        },
        {
          title: 'Community Events',
          content: 'Events shared by other AMSCompass vendors are labeled "Community." When importing an event via URL, toggle "Save to Community Events" in the final step to make it visible to everyone — a quick way to give back to the community.'
        }
      ],
      tips: [
        'Set up your Artisan Profile for personalized recommendations',
        'Search multiple regions to find events worth traveling to',
        'Check events early - application deadlines vary widely',
        'Toggle "Save to Community Events" when importing so other vendors can discover the show too'
      ]
    }
  },

  events: {
    id: 'events',
    title: 'Events',
    icon: 'Heart',
    category: 'Events & Planning',
    description: 'Manage your event calendar, track performance, and plan your year.',
    steps: [
      {
        target: '[data-testid="add-event-btn"]',
        title: 'Add Event',
        content: 'Add events you\'ve been accepted to or are planning to attend.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="event-calendar"]',
        title: 'Event Calendar',
        content: 'View your events in calendar or list format. See what\'s coming up.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="event-card"]',
        title: 'Event Details',
        content: 'Click any event to see details, add sales, expenses, and track performance.',
        placement: 'right'
      },
      {
        target: '[data-testid="event-series"]',
        title: 'Event Series',
        content: 'Link recurring annual events to track year-over-year performance.',
        placement: 'left'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Your Event Calendar',
          content: 'The Events module is your command center for show planning. Add events, track status, and see everything coming up in calendar or list view.'
        },
        {
          title: 'Event Details',
          content: 'Each event page shows all relevant info: dates, location, booth cost, and your sales/expenses. Rate events and add notes to remember key details.'
        },
        {
          title: 'Event Series',
          content: 'Many shows happen annually. Link multiple years of the same event into a Series to track performance over time. See trends, compare years, and decide whether to return.'
        },
        {
          title: 'Pre-Event Planning',
          content: 'Use the task checklist to prepare for events. Track inventory to bring, setup requirements, and last-minute to-dos.'
        },
        {
          title: 'Post-Event Review',
          content: 'After an event, record sales, add expenses, and rate your experience. These ratings inform future decisions about returning.'
        },
        {
          title: 'Community Reviews',
          content: 'When viewing a community event (an event shared with the AMSCompass community), scroll down to the Community Reviews section. Leave a 1–5 star rating and a short comment so other vendors know what to expect — foot traffic, load-in experience, organizer responsiveness, etc. You can edit or delete your review anytime.'
        },
        {
          title: 'See Something Wrong?',
          content: 'Every event detail page has a "See something wrong with this event? Report an issue" link near the bottom. Use it to email the AMSCompass team about outdated dates, broken links, or missing info — we keep the shared event data accurate together.'
        }
      ],
      tips: [
        'Add events as soon as you\'re accepted to start planning',
        'Use Event Series to track your favorite annual shows',
        'Rate and review events immediately while details are fresh',
        'Drop a quick review on community events to help other vendors — it only takes a minute'
      ]
    }
  },

  vendorCommunity: {
    id: 'vendorCommunity',
    title: 'Vendor Community',
    icon: 'Users',
    category: 'Vendor Community',
    description: 'Discover other vendors, follow your favorites, and see which shows they\'re booked for next.',
    steps: [
      {
        target: '[data-testid="community-tabs"]',
        title: 'Directory & Following',
        content: 'Switch between the full Directory of public vendors and your personalized Following feed.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="community-search"]',
        title: 'Search Vendors',
        content: 'Search by shop name, city, craft, or bio to find kindred makers.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="community-sort"]',
        title: 'Sort the Directory',
        content: 'Sort by Recently Joined, Most Active (Saved Events), or A–Z.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="follow-btn"]',
        title: 'Follow a Vendor',
        content: 'On any vendor profile, click Follow to see their upcoming shows in your Following feed.',
        placement: 'left'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'What is the Vendor Community?',
          content: 'Every AMSCompass user is a vendor with a public landing page at /v/your-slug. The Vendor Community page is where artisans discover each other, swap notes on shows, and build a network of makers.'
        },
        {
          title: 'Browsing the Directory',
          content: 'Open the Vendor Community link in the top navigation. The Directory shows every public vendor. Use the search bar to narrow by name, city, craft, or category, and the sort dropdown to control the order.'
        },
        {
          title: 'Following Vendors',
          content: 'Click any vendor card to open their public profile. Tap Follow to subscribe to their upcoming shows. Your Following tab on the Community page then aggregates every show your network has saved — an easy way to see where the community is heading next.'
        },
        {
          title: 'Sharing & Inviting',
          content: 'On your own profile (and every vendor\'s profile) the Share button copies the profile link or opens native share. Invite a Friend opens your email client with a pre-filled message and profile link — perfect for sharing with customers, collectors, or fellow makers.'
        },
        {
          title: 'Public vs Private',
          content: 'Your profile is public by default. You can flip it to private in Edit Vendor Profile → Profile Visibility. Private profiles are hidden from the Directory and return a "not found" page for anyone with the link.'
        }
      ],
      tips: [
        'Follow show organizers and repeat exhibitors at your dream venues — their upcoming shows become your hit list',
        'Invite a Friend is a great way to show customers where you\'ll be next without typing a URL',
        'Use Most Active (Saved Events) sort to find vendors who travel the same circuit you do'
      ]
    }
  },

  editProfile: {
    id: 'editProfile',
    title: 'Edit Vendor Profile',
    icon: 'Store',
    category: 'Vendor Community',
    description: 'Build your public vendor landing page — cover photo, bio, links, and a 24-photo gallery.',
    steps: [
      {
        target: '[data-testid="upload-cover-btn"]',
        title: 'Upload a Cover',
        content: 'Your cover is the horizontal banner at the top of your profile. Recommended size is 1600 × 540, up to 5MB.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="profile-photo-upload"]',
        title: 'Set a Profile Photo & Logo',
        content: 'Upload a profile photo (shown as your avatar) and an optional logo used in cards and search results.',
        placement: 'right'
      },
      {
        target: '[data-testid="edit-slug"]',
        title: 'Claim Your URL',
        content: 'Your profile lives at /v/your-slug. Use letters, numbers, and dashes only. Slugs must be unique.',
        placement: 'bottom'
      },
      {
        target: '[data-testid="upload-gallery-btn"]',
        title: 'Add Gallery Photos',
        content: 'Upload up to 24 gallery images (3MB each). These showcase your work on your public profile.',
        placement: 'left'
      },
      {
        target: '[data-testid="toggle-public-profile"]',
        title: 'Public or Private',
        content: 'Toggle Profile Visibility. Public profiles are discoverable in the Directory; private ones are hidden and unshareable.',
        placement: 'left'
      },
      {
        target: '[data-testid="save-profile-btn"]',
        title: 'Save Your Profile',
        content: 'Click Save Profile to publish your changes. Use "View Public Profile" (top right) to preview what others see.',
        placement: 'top'
      }
    ],
    fullGuide: {
      sections: [
        {
          title: 'Getting to Edit Profile',
          content: 'Click your name in the top-right of the app → choose Edit Vendor Profile. You can also reach it from the Edit Profile button on your own public page (/v/your-slug).'
        },
        {
          title: 'Cover, Logo, and Profile Photo',
          content: 'The cover photo is the large banner at the top of your landing page — go wide (1600 × 540 recommended). The profile photo is your avatar (shown as a circle overlapping the cover). The logo is used as a fallback avatar in cards and search results when you don\'t have a profile photo set.'
        },
        {
          title: 'Identity & Story',
          content: 'Your Business/Shop Name, Tagline (120 chars), Bio (up to 1,500 chars), and Vendor Category tell the community who you are. Invest a few minutes here — this is your elevator pitch to fellow makers and to any customer you share your link with.'
        },
        {
          title: 'Links',
          content: 'Add your website, Etsy shop, and as many of the six social platforms as you use (Instagram, Facebook, TikTok, Pinterest, YouTube, X/Twitter). Empty fields are automatically hidden on your public profile — you only see what you fill in.'
        },
        {
          title: 'Gallery (max 24 photos)',
          content: 'The gallery is a 24-image showcase of your work. Each photo must be 3MB or smaller. Hover over a photo to reveal the × button and remove it. When you upload, drag-and-drop any image file — the system queues multiple uploads at once (up to the 24-slot cap).'
        },
        {
          title: 'Public URL Slug',
          content: 'Your slug is the handle in your profile URL: /v/your-slug. We auto-generate one when you sign up, but you can rename it anytime. Slugs must be unique across AMSCompass — if your desired slug is taken you\'ll see a clear error.'
        },
        {
          title: 'Visibility Toggle',
          content: 'Profile Visibility controls whether you show up in the Directory and whether your /v/ link works for visitors. Flip it off for a private account or while you\'re still building out your page.'
        }
      ],
      tips: [
        'Use a high-contrast cover photo so your logo/avatar pops in the overlay',
        'Mention your craft, region, and what makes you unique in the first line of your bio — search matches on bio too',
        'Rotate gallery photos seasonally to give repeat visitors something new',
        'Save and preview often by clicking "View Public Profile" — it opens in a new tab so you don\'t lose edits'
      ]
    }
  }
};

// Get all tutorials as an array
export const getAllTutorials = () => Object.values(tutorials);

// Fallback category for any tutorial that hasn't been tagged (future-proofing).
const FALLBACK_CATEGORY = 'Other';

// Preferred display order for category chips in the Help Center UI.
const CATEGORY_ORDER = [
  'Getting Started',
  'Events & Planning',
  'Sales & Finance',
  'Vendor Community',
  'Media & Files',
  FALLBACK_CATEGORY,
];

// Return unique categories that actually have at least one tutorial,
// in a stable display order. "All" is prepended in the UI layer.
export const getTutorialCategories = () => {
  const present = new Set(
    getAllTutorials().map((t) => t.category || FALLBACK_CATEGORY)
  );
  return CATEGORY_ORDER.filter((cat) => present.has(cat));
};

// Search tutorials by keyword
export const searchTutorials = (query) => {
  const lowerQuery = query.toLowerCase();
  return getAllTutorials().filter(tutorial => {
    // Search in title and description
    if (tutorial.title.toLowerCase().includes(lowerQuery)) return true;
    if (tutorial.description.toLowerCase().includes(lowerQuery)) return true;
    
    // Search in steps
    for (const step of tutorial.steps) {
      if (step.title.toLowerCase().includes(lowerQuery)) return true;
      if (step.content.toLowerCase().includes(lowerQuery)) return true;
    }
    
    // Search in full guide
    for (const section of tutorial.fullGuide.sections) {
      if (section.title.toLowerCase().includes(lowerQuery)) return true;
      if (section.content.toLowerCase().includes(lowerQuery)) return true;
    }
    
    // Search in tips
    for (const tip of tutorial.fullGuide.tips) {
      if (tip.toLowerCase().includes(lowerQuery)) return true;
    }
    
    return false;
  });
};

// Get tutorial by ID
export const getTutorialById = (id) => tutorials[id] || null;
